import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FadeUp } from "../components/Animations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BlogList() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 px-6 md:px-16 max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
            Journal
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-onyx leading-[1.05] max-w-4xl">
            Perspectives on Design
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="text-base font-light text-onyx/60 mt-6 max-w-2xl leading-relaxed">
            Insights on architecture, materials, and the philosophy behind
            elevated interior design.
          </p>
        </FadeUp>
      </section>

      {/* Featured Post */}
      {posts.length > 0 && (
        <section className="px-6 md:px-16 max-w-[1400px] mx-auto mb-20">
          <FadeUp>
            <Link
              to={`/journal/${posts[0].slug}`}
              data-testid="blog-featured-post"
              className="group grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12"
            >
              <div className="img-scale aspect-[4/3] w-full">
                <img
                  src={posts[0].thumbnail}
                  alt={posts[0].title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-4">
                  {posts[0].category} — {posts[0].date}
                </p>
                <h2 className="font-serif text-3xl md:text-4xl font-light text-onyx group-hover:text-taupe transition-colors leading-snug mb-4">
                  {posts[0].title}
                </h2>
                <p className="text-base font-light text-onyx/60 leading-relaxed mb-6">
                  {posts[0].subtitle}
                </p>
                <span className="inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase font-light text-onyx group-hover:text-taupe transition-colors">
                  Read Article <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </FadeUp>
        </section>
      )}

      {/* Divider */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="border-t border-concrete" />
      </div>

      {/* Post Grid */}
      <section className="px-6 md:px-16 max-w-[1400px] mx-auto py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          {posts.slice(1).map((post, i) => (
            <FadeUp key={post.id} delay={i * 0.1}>
              <Link
                to={`/journal/${post.slug}`}
                data-testid={`blog-post-${i}`}
                className="group block"
              >
                <div className="img-scale aspect-[4/3] w-full mb-5">
                  <img
                    src={post.thumbnail}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs tracking-[0.2em] uppercase text-taupe mb-3">
                  {post.category} — {post.read_time}
                </p>
                <h3 className="font-serif text-xl md:text-2xl font-light text-onyx group-hover:text-taupe transition-colors leading-snug mb-3">
                  {post.title}
                </h3>
                <p className="text-sm font-light text-onyx/50 leading-relaxed line-clamp-2">
                  {post.subtitle}
                </p>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}

function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => navigate("/journal"));
    axios
      .get(`${API}/blog`)
      .then((res) => setAllPosts(res.data))
      .catch(() => {});
  }, [slug, navigate]);

  if (!post) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border border-onyx border-t-transparent animate-spin" />
      </div>
    );
  }

  const otherPosts = allPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="blog-article" className="pt-20">
      {/* Back */}
      <section className="py-8 px-6 md:px-16 max-w-[1400px] mx-auto">
        <button
          data-testid="blog-back-btn"
          onClick={() => navigate("/journal")}
          className="inline-flex items-center gap-2 text-sm tracking-[0.15em] uppercase font-light text-taupe hover:text-onyx transition-colors"
        >
          <ArrowLeft size={16} /> Back to Journal
        </button>
      </section>

      {/* Article Header */}
      <section className="px-6 md:px-16 max-w-[900px] mx-auto pb-12">
        <FadeUp>
          <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-6">
            {post.category} — {post.date} — {post.read_time}
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-onyx leading-[1.1] mb-6">
            {post.title}
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="text-lg md:text-xl font-light text-onyx/60 leading-relaxed italic font-serif">
            {post.subtitle}
          </p>
        </FadeUp>
      </section>

      {/* Hero Image */}
      <section className="px-6 md:px-16 max-w-[1200px] mx-auto mb-16">
        <FadeUp delay={0.3}>
          <div className="aspect-[21/9] w-full overflow-hidden">
            <img
              src={post.thumbnail}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </FadeUp>
      </section>

      {/* Article Body */}
      <section className="px-6 md:px-16 max-w-[720px] mx-auto pb-20">
        {post.content.map((paragraph, i) => (
          <FadeUp key={i} delay={0.1}>
            <p className="text-base md:text-lg font-light text-onyx/80 leading-[2] mb-8 first-letter:text-3xl first-letter:font-serif first-letter:font-light first-letter:text-onyx first-letter:float-left first-letter:mr-2 first-letter:mt-1">
              {paragraph}
            </p>
          </FadeUp>
        ))}

        {/* Author */}
        <div className="border-t border-concrete pt-8 mt-12">
          <p className="text-xs tracking-[0.25em] uppercase text-taupe mb-2">
            Written by
          </p>
          <p className="font-serif text-xl font-light text-onyx">
            {post.author}
          </p>
          <p className="text-sm font-light text-onyx/50 mt-1">
            Founder & Principal, J Design Haus
          </p>
        </div>
      </section>

      {/* Related Posts */}
      {otherPosts.length > 0 && (
        <section className="bg-onyx py-20 md:py-24 px-6 md:px-16">
          <div className="max-w-[1400px] mx-auto">
            <FadeUp>
              <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-10">
                Continue Reading
              </p>
            </FadeUp>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
              {otherPosts.map((p, i) => (
                <FadeUp key={p.id} delay={i * 0.15}>
                  <Link
                    to={`/journal/${p.slug}`}
                    className="group block"
                  >
                    <div className="img-scale aspect-[4/3] w-full mb-5">
                      <img
                        src={p.thumbnail}
                        alt={p.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-xs tracking-[0.2em] uppercase text-taupe mb-3">
                      {p.category} — {p.read_time}
                    </p>
                    <h3 className="font-serif text-xl md:text-2xl font-light text-bone group-hover:text-taupe transition-colors leading-snug">
                      {p.title}
                    </h3>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default function BlogPage() {
  const { slug } = useParams();
  return slug ? <BlogArticle /> : <BlogList />;
}
